import React, { useEffect, useState, ChangeEvent, useMemo, useCallback, useRef } from 'react';
import { TablePagination } from '@mui/material';
import { CustomTableBody } from '../../shared/EquipmentTable/CustomTableBody';
import { CustomTableHead } from '../../shared/EquipmentTable/CustomTableHead';
import { Columns } from '@/types';
import {
  TablePaginationContainer,
  StyledTableContainer,
  ScrollableBox,
  StyledTable,
  PaginationBox,
} from './styles';
import { getEquipmentColumnsFromAssemblyTemplate } from '@/utils';
import { useDragToScroll, useRouter } from '@/hooks';
import * as state from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import {
  GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery,
  useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery,
  ColumnKeysEnum,
  useGetUserPreferencesQuery,
  Assembly,
} from '@/__generated__/graphql';
import { useUnitConversion } from '@/hooks/useUnitConversion';
import { ColumnsSelectionDialog } from '../ColumnsSelectionDialog';
import { ErrorLogger } from '@/utils/error-logger';
import {
  INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR,
  INSIGHTS_USER_PREFERENCE_ERROR,
} from '@/constants/Errors';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import useLiveSyncAllPois from '../../shared/hooks/useLiveSyncAllPois';

export type FilteredAssembly = {
  [key: string]: string;
};

type Props = {
  selectedAssemblyIds: string[];
  setSelectedAssemblyIds: React.Dispatch<React.SetStateAction<string[]>>;
};

const getView3DColumn = (
  router: ReturnType<typeof useRouter>,
  structureId: string,
  setSelectedAssemblyId: (id: string | undefined) => void
): Columns => ({
  isSortable: false,
  visibility: true,
  key: 'view3D',
  columnName: 'View 3D',
  displayConfig: {
    type: '3DBUTTON',
  },
  onCellClick: (event, item) => {
    event.stopPropagation();
    setSelectedAssemblyId(undefined);

    const url = `/analysis/${structureId}/viewer?assembly=${item?.tagName}`;

    const isCtrlClick = event.ctrlKey || event.metaKey;
    if (isCtrlClick) window.open(url, '_blank');
    else router.push(url);
  },
});

export const EquipmentTable = ({ selectedAssemblyIds, setSelectedAssemblyIds }: Props) => {
  const [page, setPage] = useState(0);
  const [allColumns, setAllColumns] = useState<Columns[]>([]);
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);
  const setInsightsExplorerLoading = useSetAtom(state.insightsExplorerLoadingState);
  const [allAssemblies, setAllAssemblies] =
    useState<
      GetAssembliesByTemplateFieldsFiltersForFilterSideBarQuery['getAssembliesByTemplateFieldsFilters']
    >();
  const [assembliesCount, setAssembliesCount] = useAtom(state.assembliesCount);
  const [loadingTable, setLoadingTable] = useState(true);
  const setAssembliesLoading = useSetAtom(state.assembliesLoading);
  const [dialogOpen, setDialogOpen] = useState(false);
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const templateId = useAtomValue(state.selectedAssemblyTemplateId)!;
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const filteredDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const [appliedTemplateFilters, setAppliedTemplateFilters] = useState(selectedTemplateFilters);
  const [appliedDeckFilters, setAppliedDeckFilters] = useState(filteredDeckIds);
  const [appliedPaintRegionFilters, setAppliedPaintRegionFilters] =
    useState(selectedPaintRegionIds);
  const [appliedWorkpackFilters, setAppliedWorkpackFilters] = useState(selectedWorkpackIds);

  // Sync paint region filters with global state
  useEffect(() => {
    setAppliedPaintRegionFilters(selectedPaintRegionIds);
  }, [selectedPaintRegionIds]);

  const [applyFilters] = useAtom(state.applyFilters);
  const structureId = useGetStructureId();
  const [sortDirection, setSortDirection] = useState<number>(1);
  const [sortBy, setSortBy] = useState('createdAt');
  const sortByReference = useRef(sortBy);
  const { data: userPreferenceData, error: userPreferenceError } = useGetUserPreferencesQuery({
    fetchPolicy: 'cache-first',
  });
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const [selectedAssemblyName, setSelectedAssemblyName] = useAtom(state.selectedAssemblyName);
  const { getUnitSuffix } = useUnitConversion();

  // Use the callback to handle refetch completion since we are using a lazy query
  // which does not trigger onCompleted when the document is refetched
  useLiveSyncAllPois(() => {
    if (structureId && templateId) {
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  });

  const { containerReference, handleMouseDown } = useDragToScroll();

  const [fetchFilteredAssemblies] =
    useGetAssembliesByTemplateFieldsFiltersForFilterSideBarLazyQuery({
      onCompleted: (data) => {
        // Clear previous data before setting new data
        setAllAssemblies([]);
        setAllAssemblies(data?.getAssembliesByTemplateFieldsFilters);
        setAssembliesCount(data?.getAssembliesCount?.count ?? 0);
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
      },
      onError: (err: Error) => {
        ErrorLogger.log(
          INSIGHTS_GET_FILTERED_ASSEMBLIES_ERROR.withContext({
            input: {
              selectedTemplateFilters: appliedTemplateFilters,
              structureId,
              templateId,
              selectedDeckIds: appliedDeckFilters,
              selectedPaintRegionIds: appliedPaintRegionFilters,
              selectedWorkpackIds: appliedWorkpackFilters,
            },
            pageInfo: {
              skip: page * rowsPerPage,
              limit: rowsPerPage,
              sortBy,
              sortDir: sortDirection,
            },
          }).withError(err)
        );
        setInsightsExplorerLoading(false);
        setLoadingTable(false);
      },
    });
  const getFilteredAssemblies = useCallback(() => {
    if (structureId && templateId) {
      setAssembliesLoading(true);
      fetchFilteredAssemblies({
        variables: {
          input: {
            selectedTemplateFilters: appliedTemplateFilters,
            structureId,
            templateId,
            selectedDeckIds: appliedDeckFilters,
            selectedPaintRegionIds: appliedPaintRegionFilters,
            selectedWorkpackIds: appliedWorkpackFilters,
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy,
            sortDir: sortDirection,
          },
        },
      });
    }
  }, [
    appliedDeckFilters,
    appliedPaintRegionFilters,
    appliedTemplateFilters,
    appliedWorkpackFilters,
    fetchFilteredAssemblies,
    page,
    rowsPerPage,
    setAssembliesLoading,
    sortBy,
    sortDirection,
    structureId,
    templateId,
  ]);

  useEffect(() => {
    if (templateId && applyFilters) {
      getFilteredAssemblies();
    }
  }, [applyFilters, getFilteredAssemblies, templateId]);

  useEffect(() => {
    if (sortByReference.current !== sortBy) {
      getFilteredAssemblies();
    }
  }, [getFilteredAssemblies, sortBy]);

  useEffect(() => {
    getFilteredAssemblies();
  }, [getFilteredAssemblies, rowsPerPage, page]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedTemplateFilters(selectedTemplateFilters);
      setAppliedDeckFilters(filteredDeckIds);
      setAppliedPaintRegionFilters(selectedPaintRegionIds);
      setAppliedWorkpackFilters(selectedWorkpackIds);
      setPage(0);
    }
  }, [
    templateId,
    selectedTemplateFilters,
    filteredDeckIds,
    selectedPaintRegionIds,
    selectedWorkpackIds,
    applyFilters,
    fetchFilteredAssemblies,
    structureId,
    page,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (userPreferenceError) {
      ErrorLogger.log(
        INSIGHTS_USER_PREFERENCE_ERROR.withContext({ structureId }).withError(userPreferenceError)
      );
    }
    const preferencesList = userPreferenceData?.getUserPreferences?.structure[structureId];
    const userColumns = preferencesList?.columns[ColumnKeysEnum.Component] ?? [];
    const updatedColumns = [...userColumns];

    if (!updatedColumns.includes('Component')) {
      updatedColumns.push('Component');
    }

    setVisibleColumns(updatedColumns);
  }, [structureId, userPreferenceData?.getUserPreferences?.structure, userPreferenceError]);

  const router = useRouter();

  const initialColumns = useMemo(() => {
    if (!assemblyTemplate) return [];

    const columnsFromTemplate = getEquipmentColumnsFromAssemblyTemplate(assemblyTemplate);
    const view3dColumn = [getView3DColumn(router, structureId, setSelectedAssemblyId)];

    return [
      {
        isSortable: true,
        visibility: true,
        key: 'tagName',
        columnName: 'Component',
      },
      ...columnsFromTemplate,
      ...view3dColumn,
    ] as Columns[];
  }, [assemblyTemplate, router, structureId, setSelectedAssemblyId]);

  useEffect(() => {
    const defaultColumns =
      visibleColumns.length > 0
        ? visibleColumns
        : initialColumns.filter(({ visibility }) => visibility).map(({ columnName }) => columnName);

    setAllColumns(() =>
      initialColumns.map((column) => ({
        ...column,
        visibility: defaultColumns.includes(column.columnName),
      }))
    );
  }, [initialColumns, visibleColumns]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const toggleDialog = useCallback(() => {
    setDialogOpen(!dialogOpen);
  }, [dialogOpen]);

  const handleRowCheckBoxChange = useCallback(
    (assemblyId: string) => {
      if (selectedAssemblyIds.includes(assemblyId)) {
        setSelectedAssemblyIds(
          selectedAssemblyIds.filter((selectedId) => selectedId !== assemblyId)
        );
      } else {
        setSelectedAssemblyIds([...selectedAssemblyIds, assemblyId]);
      }
    },
    [selectedAssemblyIds, setSelectedAssemblyIds]
  );

  const headerCheckBoxChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked && allAssemblies?.length) {
        setSelectedAssemblyIds(allAssemblies.map((assembly) => assembly.id));
      } else {
        setSelectedAssemblyIds([]);
      }
    },
    [setSelectedAssemblyIds, allAssemblies]
  );

  const handleSort = useCallback(
    (property: string) => {
      const isAsc = sortBy === property && sortDirection === 1;
      setSortDirection(isAsc ? -1 : 1);
      setSortBy(property);
    },
    [sortDirection, sortBy]
  );

  const activeColumns = useMemo(() => {
    return allColumns
      .filter(({ visibility }) => visibility)
      .map((column) => {
        const { displayUnit, unit, columnName, templateFieldKey } = column;
        const unitSuffix = getUnitSuffix({
          unit: displayUnit ?? unit,
          field: templateFieldKey,
        });
        let upperCaseColumnName = columnName.toUpperCase();
        upperCaseColumnName = unitSuffix
          ? `${upperCaseColumnName} (${unitSuffix})`
          : upperCaseColumnName;
        return { ...column, columnName: upperCaseColumnName };
      });
  }, [allColumns, getUnitSuffix]);

  const dialogColumns = useMemo(() => {
    return allColumns.map(({ columnName, visibility, displayUnit, unit, templateFieldKey }) => {
      return {
        name: columnName,
        key: columnName,
        isSelected: visibility,
        displayUnit: displayUnit ?? '',
        unit: unit ?? '',
        templateFieldKey,
      };
    });
  }, [allColumns]);

  const handleApplyColumns = useCallback((selectedColumnsKeys: string[]) => {
    setVisibleColumns(selectedColumnsKeys);
    setAllColumns((previous) =>
      previous.map((column) => ({
        ...column,
        visibility: selectedColumnsKeys.includes(column.columnName ?? ''),
      }))
    );
  }, []);

  const handleRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, assembly: Assembly) => {
      if ((event.target as HTMLInputElement).type === 'checkbox') {
        return;
      }
      if (selectedAssemblyId === assembly.id) {
        setSelectedAssemblyId(undefined);
        return;
      }
      if (selectedAssemblyName === assembly.tagName) {
        setSelectedAssemblyName(undefined);
        return;
      }
      setSelectedAssemblyId(assembly.id);
      setSelectedAssemblyName(assembly.tagName);
    },
    [selectedAssemblyId, selectedAssemblyName, setSelectedAssemblyId, setSelectedAssemblyName]
  );

  return (
    <>
      <ColumnsSelectionDialog
        open={dialogOpen}
        columns={dialogColumns}
        heading="Enable the columns you would like to see in the table"
        subHeading="Select the columns you want to display:"
        applyButtonText="Apply"
        userPreferenceKey={ColumnKeysEnum.Component}
        onApply={handleApplyColumns}
        onClose={() => setDialogOpen(false)}
      />

      <StyledTableContainer>
        <ScrollableBox ref={containerReference} onMouseDown={handleMouseDown}>
          <StyledTable stickyHeader>
            <CustomTableHead
              orderBy={sortBy}
              order={sortDirection}
              activeColumns={activeColumns}
              toggleDialog={toggleDialog}
              handleHeaderCheckboxChange={headerCheckBoxChange}
              handleSort={handleSort}
            />

            <CustomTableBody<Assembly>
              loading={loadingTable}
              data={allAssemblies as Assembly[]}
              activeColumns={activeColumns}
              onCheckboxChange={handleRowCheckBoxChange}
              selectedRow={selectedAssemblyId}
              onRowClick={handleRowClick}
              selectedIds={selectedAssemblyIds}
            />
          </StyledTable>
        </ScrollableBox>
        <PaginationBox>
          <TablePaginationContainer>
            <TablePagination
              count={assembliesCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPageOptions={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
                { label: 'All', value: assembliesCount },
              ]}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              data-testid="equipment-table-pagination"
            />
          </TablePaginationContainer>
        </PaginationBox>
      </StyledTableContainer>
    </>
  );
};
